import React from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import GameCard from '../components/GameCard/GameCard';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator/PullToRefreshIndicator';
import GameDetailsOverlay from '../components/GameDetailsOverlay/GameDetailsOverlay';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { usePageCache } from '../hooks/usePageCache';
import { fetchLibraryGames, claimFreeGameBySlug } from '../api/games';

export default function MarketPage({ onOpenGame = () => { } }) {
  const [games, setGames] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [total, setTotal] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);
  const [selectedGame, setSelectedGame] = React.useState(null);
  // Временно скрываем поиск и фильтр
  // const [currentSort, setCurrentSort] = React.useState('created_desc');
  // const [searchQuery, setSearchQuery] = React.useState('');

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { slug } = useParams();
  const pageSize = 24;
  const { getCachedData } = usePageCache();

  // Функция загрузки игр (упрощенная без поиска и фильтра)
  const loadGames = React.useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const result = await fetchLibraryGames({
        page,
        pageSize,
        sort: 'created_desc', // Фиксированная сортировка
        q: '' // Пустой поиск
      });

      setGames(result.items);
      setTotal(result.total);
      setCurrentPage(result.page);
      return result;
    } catch (e) {
      console.error('Error loading games:', e);
      setError(e.message || String(e));
      throw e;
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  // Используем кэш для первой загрузки
  const cachedData = getCachedData('marketplace', () => loadGames(1), []);

  // Обновляем состояние из кэша при первой загрузке
  React.useEffect(() => {
    if (cachedData.data && isInitialLoad) {
      setGames(cachedData.data.items);
      setTotal(cachedData.data.total);
      setCurrentPage(cachedData.data.page);
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, [cachedData.data, isInitialLoad]);

  // Обновляем состояние загрузки из кэша
  React.useEffect(() => {
    if (cachedData.loading !== undefined) {
      setLoading(cachedData.loading);
    }
  }, [cachedData.loading]);

  // Обработка открытия игры через URL параметры (slug)
  React.useEffect(() => {
    if (slug && games.length > 0) {
      const game = games.find(g => g.slug === slug);
      if (game) {
        const modalData = transformGameForModal(game);
        setSelectedGame(modalData);
      }
    }
  }, [slug, games]);

  // Если базовый путь без slug — убеждаемся, что модалка закрыта
  React.useEffect(() => {
    if (!slug && selectedGame) {
      setSelectedGame(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Временно скрываем обработчики поиска и фильтра
  /*
  const handleSortChange = (newSort) => {
    setCurrentSort(newSort);
    loadGames(1, newSort, searchQuery);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    loadGames(1, currentSort, query);
  };
  */

  const handlePageChange = (newPage) => {
    loadGames(newPage);
  };

  // Функция для pull-to-refresh
  const handleRefresh = React.useCallback(async () => {
    await cachedData.refresh();
  }, [cachedData]);

  // Настройка pull-to-refresh
  const {
    elementRef,
    isRefreshing,
    isPulling,
    pullDistance,
    refreshProgress,
    canRefresh
  } = usePullToRefresh(handleRefresh, {
    threshold: 80,
    resistance: 2.5,
    maxPullDistance: 120,
    enabled: !loading && !error
  });

  async function onClaim(slug) {
    try {
      await claimFreeGameBySlug(slug);
      // Обновим список
      loadGames(currentPage);
    } catch (e) {
      alert(e.message || String(e));
    }
  }

  // Преобразуем данные из Supabase в формат для GameCard
  const transformGameForCard = (game) => {
    const isFree = (game.price_cents || 0) <= 0;
    return {
      id: game.id,
      title: game.title,
      subtitle: game.subtitle, // используем subtitle вместо description
      coverUrl: game.coverUrl, // thumb for cards
      status: isFree ? 'free' : 'paid',
      slug: game.slug,
      priceCents: game.price_cents || 0,
      currency: game.currency || 'RUB',
      isFree,
      subscriptionIncluded: game.included_in_subscription
    };
  };

  // Преобразуем данные для модалки с полной обложкой
  const transformGameForModal = (game) => {
    const isFree = (game.price_cents || 0) <= 0;
    return {
      id: game.id,
      title: game.title,
      subtitle: game.subtitle,
      description: game.description, // Добавляем описание для модалки
      coverUrl: game.fullCoverUrl, // full cover for modal
      status: isFree ? 'free' : 'paid',
      slug: game.slug,
      priceCents: game.price_cents || 0,
      currency: game.currency || 'RUB',
      isFree,
      subscriptionIncluded: game.included_in_subscription,
      ozonLink: game.ozon_link || null
    };
  };

  // Обработчик открытия игры - открываем модалку
  const handleOpenGame = (game) => {
    const originalGame = games.find(g => g.id === game.id);
    if (originalGame) {
      const modalData = transformGameForModal(originalGame);
      setSelectedGame(modalData);
      navigate(`/marketplace/${originalGame.slug}`);
    }
  };

  // Обработчики для модалки
  const handleCloseGame = () => {
    setSelectedGame(null);
    navigate('/marketplace');
  };

  const handleShare = () => {
    // TODO: Реализовать функционал шаринга
  };

  const handlePlay = () => {
    if (selectedGame) {
      navigate(`/game/${selectedGame.id}`);
    }
  };

  // Обработка открытия игры через query параметр (для совместимости)
  React.useEffect(() => {
    const gameSlug = searchParams.get('game');
    if (gameSlug && games.length > 0) {
      const game = games.find(g => g.slug === gameSlug);
      if (game) {
        const modalData = transformGameForModal(game);
        setSelectedGame(modalData);
        navigate(`/marketplace/${game.slug}`);
      }
    }
  }, [searchParams, games, navigate]);

  // Скелетон для загрузки
  if (loading) return (
    <main className="page">
      <h1>Все игры</h1>

      {/* Скелетон для 12-24 карточек */}
      <div className="game-list">
        {Array.from({ length: 12 }, (_, i) => (
          <div key={i}>
            <div className="skeleton-card" style={{ height: '100px' }} />
            {i !== 11 && <div className="divider" />}
          </div>
        ))}
      </div>
    </main>
  );

  // Обработка ошибок
  if (error) return (
    <main className="page">
      <h1>Все игры</h1>
      <div style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
        <h3>Ошибка загрузки игр</h3>
        <p>{error}</p>
        {error.includes('RLS') && (
          <p style={{ fontSize: '0.9rem', marginTop: '1rem' }}>
            <strong>Подсказка:</strong> Проверьте RLS политику для чтения games (status='published' and visibility='public').
          </p>
        )}
        <button
          onClick={() => loadGames(1)}
          style={{ marginTop: '1rem', padding: '0.5rem 1rem' }}
        >
          Попробовать снова
        </button>
      </div>
    </main>
  );

  // Пустое состояние
  if (games.length === 0 && !loading) {
    return (
      <main className="page">
        <h1>Все игры</h1>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <p>Пока нет доступных игр</p>
        </div>
      </main>
    );
  }

  return (
    <>
      <PullToRefreshIndicator
        isRefreshing={isRefreshing}
        isPulling={isPulling}
        progress={refreshProgress}
        canRefresh={canRefresh}
        pullDistance={pullDistance}
      />
      <main className="page" ref={elementRef}>
        <h1>Все игры</h1>

        {/* Временно скрываем контролы для сортировки и поиска */}
        {/*
        <div style={{ marginBottom: '1rem', display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <select 
            value={currentSort} 
            onChange={(e) => handleSortChange(e.target.value)}
            style={{ padding: '0.5rem' }}
          >
            <option value="created_desc">Сначала новые</option>
            <option value="title_asc">По названию</option>
            <option value="popular">Популярные</option>
          </select>
          
          <input
            type="text"
            placeholder="Поиск игр..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch(e.target.value)}
            style={{ padding: '0.5rem', flex: 1, maxWidth: '300px' }}
          />
          
          {searchQuery && (
            <button onClick={() => handleSearch('')}>Очистить</button>
          )}
        </div>
        */}

        {/* Список игр */}
        <section className="game-list">
          {games.map((game, idx) => {
            const cardData = transformGameForCard(game);
            return (
              <div key={game.id}>
                <GameCard {...cardData} onClick={() => handleOpenGame(cardData)} onClaim={onClaim} />
                {idx !== games.length - 1 && <div className="divider" />}
              </div>
            );
          })}
        </section>

        {/* Пагинация */}
        {total > pageSize && (
          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <div style={{ marginBottom: '1rem' }}>
              Страница {currentPage} из {Math.ceil(total / pageSize)}
              (всего {total} игр)
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
              {currentPage > 1 && (
                <button onClick={() => handlePageChange(currentPage - 1)}>
                  ← Предыдущая
                </button>
              )}
              {currentPage < Math.ceil(total / pageSize) && (
                <button onClick={() => handlePageChange(currentPage + 1)}>
                  Следующая →
                </button>
              )}
            </div>
          </div>
        )}


      </main>

      {/* Модалка с деталями игры */}
      {selectedGame && (
        <GameDetailsOverlay
          game={selectedGame}
          onClose={handleCloseGame}
          onShare={handleShare}
          onPlay={handlePlay}
        />
      )}

    </>
  );
}
